import React, { useMemo, useState } from 'react';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { Badge } from 'flowbite-react';
import { Change } from '../../models';
import { Button, EmailAuthBadges, Icon, RequesterEmails, TriageRowActions } from '..';
import { htmlToPlainPreview } from '../../utils/htmlToPlainPreview';
import { sortTriageRows } from '../../utils/sortTriageRows';
import {
    TRIAGE_CONFIGURABLE_COLUMNS,
    TRIAGE_SORTABLE_COLUMNS,
    TriageColumnId,
    TriageDensity,
    TriageViewPrefs,
    moveColumn,
    toggleColumnVisibility,
} from '../../utils/triageViewPrefs';
import {
    handleChangeMiddleClick,
    preventMiddleClickAutoscroll,
} from '../../utils/openChangeInNewBrowserTab';
import { classifySpamSource } from '../../utils/spamSource';

export type TriageListViewProps = {
    rows: Change[];
    prefs: TriageViewPrefs;
    selectedChangeIds: string[];
    allSelected: boolean;
    someSelected: boolean;
    onToggleSelectAll: () => void;
    language: string;
    summaryPreviews?: Record<string, string>;
    onPrefsChange: (prefs: TriageViewPrefs) => void;
    onToggleSelect: (changeId: string) => void;
    onOpen: (item: Change) => void;
    onTake: (item: Change) => void;
    onModify?: (item: Change) => void;
    onMerge?: (item: Change) => void;
    onResolve?: (item: Change) => void;
    onDiscard?: (item: Change) => void;
    onRestore?: (item: Change) => void;
    onMarkSafe?: (item: Change) => void;
    onTrustSender?: (item: Change) => void;
    onTrustDomain?: (item: Change) => void;
    canTakeCard?: (item: Change) => boolean;
    isTrashView?: boolean;
};

const columnLabelKey = (column: TriageColumnId): string =>
    `priorities.list.columns.${column}`;

function typeLabelOf(item: Change): string {
    return String(item.typeSubtype || item.typeDomain || item.type || '').trim();
}

export { sortTriageRows } from '../../utils/sortTriageRows';

/**
 * Dense RT-like table for triage: selectable rows, sortable headers, column picker.
 */
function TriageListView(props: TriageListViewProps) {
    const {
        rows, prefs, selectedChangeIds, allSelected, someSelected, onToggleSelectAll, language,
        summaryPreviews, onPrefsChange, onToggleSelect, onOpen, onTake,
        onModify, onMerge, onResolve, onDiscard, onRestore, onMarkSafe,
        onTrustSender, onTrustDomain,
        canTakeCard = () => true, isTrashView = false,
    } = props;
    const { t } = useTranslation();
    const [columnsOpen, setColumnsOpen] = useState(false);

    // Parent already sorts the full queue before pagination; re-sort keeps header clicks consistent
    // if a stale page slice is briefly shown.
    const sortedRows = useMemo(
        () => sortTriageRows(rows, prefs.sort, language),
        [rows, prefs.sort, language]
    );

    const density: TriageDensity = prefs.density;
    const cellPad = density === 'compact' ? 'px-2 py-1' : 'px-3 py-2';
    const textSize = density === 'compact' ? 'text-xs' : 'text-sm';

    const onSortClick = (column: TriageColumnId) => {
        if (!TRIAGE_SORTABLE_COLUMNS.includes(column))
            return;
        const same = prefs.sort.column === column;
        onPrefsChange({
            ...prefs,
            sort: {
                column,
                direction: same && prefs.sort.direction === 'desc' ? 'asc' : 'desc',
            },
        });
    };

    const renderCell = (item: Change, column: TriageColumnId) => {
        switch (column) {
            case 'slug':
                return (
                    <Badge color={item.color()} size="xs" className="w-fit whitespace-nowrap">
                        {item.slugName()}
                    </Badge>
                );
            case 'priority':
                return <span className="font-semibold text-indigo-700">{item.priority}</span>;
            case 'title': {
                const title = item.localizedTitle(language) || '';
                const raw = (item.summary || item.localizedDescription(language) || '').trim();
                const preview = summaryPreviews?.[item._id]
                    ?? (raw ? htmlToPlainPreview(raw, density === 'compact' ? 100 : 160) : '');
                const isSpam = item.progress?.status === Change.STATUS_SPAM_SUSPECT;
                const spamSourceBadge = isSpam ? classifySpamSource(item.intake?.spamSource) : null;
                return (
                    <div className="min-w-0">
                        <div className="flex min-w-0 items-center gap-1.5">
                            <div className="truncate font-medium text-gray-900 dark:text-gray-100">{title}</div>
                            {spamSourceBadge && (
                                <Badge color={spamSourceBadge.color} size="xs" className="shrink-0 w-fit">
                                    {t(spamSourceBadge.labelKey)}
                                </Badge>
                            )}
                        </div>
                        {preview && (
                            <div className="truncate text-[11px] text-gray-500 dark:text-gray-400">{preview}</div>
                        )}
                        {item.intake?.headers && prefs.showEmailHeaders && (
                            <EmailAuthBadges intake={item.intake} compact className="mt-1" />
                        )}
                    </div>
                );
            }
            case 'requester':
                return <RequesterEmails source={item} className="truncate text-gray-700 dark:text-gray-200 text-xs" />;
            case 'project':
                return <span className="truncate text-gray-700 dark:text-gray-200">{item.project?.name || '—'}</span>;
            case 'type':
                return <span className="truncate text-gray-700 dark:text-gray-200">{typeLabelOf(item) || '—'}</span>;
            case 'updatedAt':
                return (
                    <span className="whitespace-nowrap text-gray-600 dark:text-gray-300">
                        {item.updatedAt ? moment(item.updatedAt).format('dd DD MMM YYYY HH:mm') : '—'}
                    </span>
                );
            case 'actions': {
                const isSpam = item.progress?.status === Change.STATUS_SPAM_SUSPECT;
                const senderEmail = item.intake?.sender?.email || '';
                const senderDomain = item.intake?.sender?.domain
                    || (senderEmail.includes('@') ? senderEmail.split('@').pop() : '');
                return (
                    <TriageRowActions
                        showTake={!isTrashView && canTakeCard(item)}
                        showModify={!isTrashView && !isSpam}
                        showMerge={!isTrashView && !isSpam && !!item.technicalContext?.mergeReview?.candidateId}
                        showResolve={!isTrashView && !isSpam}
                        showDiscard={!isTrashView}
                        showRestore={isTrashView}
                        showMarkSafe={!isTrashView && isSpam}
                        showTrustSender={!isTrashView && isSpam && !!senderEmail}
                        showTrustDomain={!isTrashView && isSpam && !!senderDomain}
                        trustSenderTitle={senderEmail
                            ? `${t('priorities.trust_sender')} — ${senderEmail}`
                            : undefined}
                        trustDomainTitle={senderDomain
                            ? `${t('priorities.trust_domain')} — ${senderDomain}`
                            : undefined}
                        onTake={() => onTake(item)}
                        onModify={() => onModify?.(item)}
                        onMerge={() => onMerge?.(item)}
                        onResolve={() => onResolve?.(item)}
                        onDiscard={() => onDiscard?.(item)}
                        onRestore={() => onRestore?.(item)}
                        onMarkSafe={() => onMarkSafe?.(item)}
                        onTrustSender={() => onTrustSender?.(item)}
                        onTrustDomain={() => onTrustDomain?.(item)}
                    />
                );
            }
            default:
                return null;
        }
    };

    return (
        <div className="relative rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm">
            <div className="flex items-center justify-end gap-2 border-b border-gray-100 dark:border-gray-700 px-2 py-1.5">
                <div className="relative">
                    <button
                        type="button"
                        className="inline-flex items-center gap-1 rounded border border-gray-200 dark:border-gray-700 px-2 py-1 text-xs text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 dark:hover:bg-gray-900"
                        onClick={() => setColumnsOpen((open) => !open)}
                    >
                        <Icon type="setting" size={3} />
                        {t('priorities.list.columns_menu')}
                    </button>
                    {columnsOpen && (
                        <div className="absolute right-0 top-full z-10 mt-1 w-64 rounded border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-2 shadow-lg">
                            <div className="mb-1 text-xs font-semibold text-gray-700 dark:text-gray-200">
                                {t('priorities.list.columns_menu')}
                            </div>
                            <ul className="max-h-72 space-y-1 overflow-auto">
                                {TRIAGE_CONFIGURABLE_COLUMNS.map((columnId) => {
                                    const visible = prefs.columns.includes(columnId);
                                    return (
                                        <li key={columnId} className="flex items-center gap-1 text-xs">
                                            <input
                                                type="checkbox"
                                                className="h-3.5 w-3.5"
                                                checked={visible}
                                                onChange={(e) => onPrefsChange({
                                                    ...prefs,
                                                    columns: toggleColumnVisibility(
                                                        prefs.columns,
                                                        columnId,
                                                        e.target.checked
                                                    ),
                                                })}
                                            />
                                            <span className="flex-1 truncate">
                                                {t(columnLabelKey(columnId))}
                                            </span>
                                            <button
                                                type="button"
                                                className="rounded px-1 text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 dark:hover:bg-gray-800 disabled:opacity-30"
                                                disabled={!visible || columnId === 'actions'}
                                                onClick={() => onPrefsChange({
                                                    ...prefs,
                                                    columns: moveColumn(prefs.columns, columnId, 'up'),
                                                })}
                                                title={t('priorities.list.move_up') as string}
                                            >
                                                <Icon type="up" size={3} />
                                            </button>
                                            <button
                                                type="button"
                                                className="rounded px-1 text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 dark:hover:bg-gray-800 disabled:opacity-30"
                                                disabled={!visible || columnId === 'actions'}
                                                onClick={() => onPrefsChange({
                                                    ...prefs,
                                                    columns: moveColumn(prefs.columns, columnId, 'down'),
                                                })}
                                                title={t('priorities.list.move_down') as string}
                                            >
                                                <Icon type="down" size={3} />
                                            </button>
                                        </li>
                                    );
                                })}
                            </ul>
                            <p className="mt-2 border-t border-gray-100 dark:border-gray-700 pt-2 text-[10px] text-gray-500 dark:text-gray-400">
                                {t('priorities.list.columns_always_visible')}
                            </p>
                        </div>
                    )}
                </div>
            </div>

            <div className="overflow-x-auto">
                <table className={`w-full border-collapse ${textSize}`}>
                    <thead>
                        <tr className="border-b border-gray-200 dark:border-gray-700 bg-slate-50 dark:bg-gray-800 text-left text-gray-600 dark:text-gray-300">
                            <th className={`${cellPad} w-8`}>
                                <input
                                    type="checkbox"
                                    className="h-3.5 w-3.5 cursor-pointer"
                                    checked={allSelected}
                                    ref={(el) => { if (el) el.indeterminate = someSelected; }}
                                    onChange={onToggleSelectAll}
                                    title={t('priorities.select_all') as string}
                                    aria-label={t('priorities.select_all') as string}
                                />
                            </th>
                            {prefs.columns.map((column) => {
                                const sortable = TRIAGE_SORTABLE_COLUMNS.includes(column);
                                const active = prefs.sort.column === column;
                                return (
                                    <th key={column} className={`${cellPad} whitespace-nowrap font-semibold`}>
                                        {sortable ? (
                                            <button
                                                type="button"
                                                className="inline-flex items-center gap-1 hover:text-gray-900 dark:text-gray-100"
                                                onClick={() => onSortClick(column)}
                                            >
                                                {t(columnLabelKey(column))}
                                                {active && (
                                                    <Icon
                                                        type={prefs.sort.direction === 'asc' ? 'up' : 'down'}
                                                        size={3}
                                                    />
                                                )}
                                            </button>
                                        ) : (
                                            t(columnLabelKey(column))
                                        )}
                                    </th>
                                );
                            })}
                        </tr>
                    </thead>
                    <tbody>
                        {sortedRows.map((item, index) => {
                            const selected = selectedChangeIds.includes(item._id);
                            const zebra = index % 2 === 1 ? 'bg-slate-50/80 dark:bg-gray-800/80' : 'bg-white dark:bg-gray-800';
                            return (
                                <tr
                                    key={item._id}
                                    className={`cursor-pointer border-b border-gray-100 dark:border-gray-700 hover:bg-blue-50/60 dark:hover:bg-blue-950/30 ${zebra} ${selected ? 'bg-blue-50 dark:bg-blue-950/40' : ''}`}
                                    onClick={() => onOpen(item)}
                                    onMouseDown={preventMiddleClickAutoscroll}
                                    onAuxClick={(event) => handleChangeMiddleClick(event, item)}
                                >
                                    <td className={cellPad} onClick={(e) => e.stopPropagation()}>
                                        <input
                                            type="checkbox"
                                            className="h-3.5 w-3.5 cursor-pointer"
                                            checked={selected}
                                            onChange={() => onToggleSelect(item._id)}
                                        />
                                    </td>
                                    {prefs.columns.map((column) => (
                                        <td
                                            key={column}
                                            className={`${cellPad} max-w-[20rem] align-middle ${column === 'actions' ? 'cursor-default' : ''}`}
                                            onClick={
                                                (column === 'actions' || column === 'requester')
                                                    ? ((e) => e.stopPropagation())
                                                    : undefined
                                            }
                                        >
                                            {renderCell(item, column)}
                                        </td>
                                    ))}
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

export default TriageListView;
